import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { startOfYearIso } from '../lib/citComputation'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>): any => (supabase as any).rpc(fn, args)

const toNum = (v: string | number | null | undefined): number =>
  v === null || v === undefined ? 0 : typeof v === 'number' ? v : parseFloat(v)

// =============================================================================
// Adjustment line catalogue
// =============================================================================
// The order here drives the display in CitAdjustmentsSection. line_type values
// MUST match the cit_adjustments.line_type CHECK constraint (mig 152).

export type CitAdjustmentLineType =
  | 'depreciation_addback'
  | 'donations_disallowable'
  | 'entertainment_disallowable'
  | 'penalties_disallowable'
  | 'general_provisions_disallowable'
  | 'bad_debt_provision_disallowable'
  | 'other_disallowable'
  | 'capital_allowance'
  | 'investment_allowance'
  | 'loss_relief'
  | 'other_allowable'

export type CitAdjustmentDirection = 'disallowable' | 'allowable'

interface CitAdjustmentCatalogEntry {
  lineType: CitAdjustmentLineType
  direction: CitAdjustmentDirection
  displayOrder: number
  defaultLabel: string
  sourceAccountCodes: string[]   // empty = no auto-compute source
}

export const CIT_ADJUSTMENT_CATALOG: readonly CitAdjustmentCatalogEntry[] = [
  // Disallowables (added back to PBT)
  { lineType: 'depreciation_addback',           direction: 'disallowable', displayOrder: 1, defaultLabel: 'Depreciation',                  sourceAccountCodes: ['6241'] },
  { lineType: 'donations_disallowable',         direction: 'disallowable', displayOrder: 2, defaultLabel: 'Donations',                     sourceAccountCodes: ['6402'] },
  { lineType: 'entertainment_disallowable',     direction: 'disallowable', displayOrder: 3, defaultLabel: 'Entertainment & Gifts',         sourceAccountCodes: ['6403'] },
  { lineType: 'penalties_disallowable',         direction: 'disallowable', displayOrder: 4, defaultLabel: 'Penalties & Fines',             sourceAccountCodes: ['6404'] },
  { lineType: 'bad_debt_provision_disallowable',direction: 'disallowable', displayOrder: 5, defaultLabel: 'Bad Debt Provision (general)',  sourceAccountCodes: ['6423'] },
  { lineType: 'general_provisions_disallowable',direction: 'disallowable', displayOrder: 6, defaultLabel: 'General Provisions',            sourceAccountCodes: [] },
  { lineType: 'other_disallowable',             direction: 'disallowable', displayOrder: 7, defaultLabel: 'Other Disallowable',            sourceAccountCodes: [] },
  // Allowables (deducted from PBT)
  { lineType: 'capital_allowance',              direction: 'allowable',    displayOrder: 1, defaultLabel: 'Capital Allowance',             sourceAccountCodes: [] },
  { lineType: 'investment_allowance',           direction: 'allowable',    displayOrder: 2, defaultLabel: 'Investment Allowance',          sourceAccountCodes: [] },
  { lineType: 'loss_relief',                    direction: 'allowable',    displayOrder: 3, defaultLabel: 'Loss Relief (brought forward)', sourceAccountCodes: [] },
  { lineType: 'other_allowable',                direction: 'allowable',    displayOrder: 4, defaultLabel: 'Other Allowable',               sourceAccountCodes: [] },
] as const

export function catalogFor(lineType: CitAdjustmentLineType): CitAdjustmentCatalogEntry {
  const entry = CIT_ADJUSTMENT_CATALOG.find(e => e.lineType === lineType)
  if (!entry) throw new Error(`Unknown cit_adjustment line_type: ${lineType}`)
  return entry
}

// =============================================================================
// Adjustment row shape (DB ↔ camelCase)
// =============================================================================

export interface CitAdjustmentRow {
  id: string
  tenantId: string
  periodEndDate: string
  lineType: CitAdjustmentLineType
  direction: CitAdjustmentDirection
  displayOrder: number
  label: string
  computedValue: number
  manualValue: number | null
  effectiveValue: number
  sourceAccountCodes: string[] | null
  notes: string | null
  accrualId: string | null
  createdAt: string
  updatedAt: string
}

interface DbAdjustmentRow {
  id: string
  tenant_id: string
  period_end_date: string
  line_type: CitAdjustmentLineType
  direction: CitAdjustmentDirection
  display_order: number
  label: string
  computed_value: string | number
  manual_value: string | number | null
  effective_value: string | number
  source_account_codes: string[] | null
  notes: string | null
  accrual_id: string | null
  created_at: string
  updated_at: string
}

function rowFromDb(r: DbAdjustmentRow): CitAdjustmentRow {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    periodEndDate: r.period_end_date,
    lineType: r.line_type,
    direction: r.direction,
    displayOrder: r.display_order,
    label: r.label,
    computedValue: toNum(r.computed_value),
    manualValue: r.manual_value === null ? null : toNum(r.manual_value),
    effectiveValue: toNum(r.effective_value),
    sourceAccountCodes: r.source_account_codes,
    notes: r.notes,
    accrualId: r.accrual_id,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  }
}

// =============================================================================
// Auto-compute: read net GL movement per source account between YTD start and
// period end. Year-to-date, posted, non-voided entries only.
// =============================================================================

export type AutoComputeDefaults = Record<CitAdjustmentLineType, number>

function emptyDefaults(): AutoComputeDefaults {
  const out = {} as AutoComputeDefaults
  for (const entry of CIT_ADJUSTMENT_CATALOG) out[entry.lineType] = 0
  return out
}

export function useAutoComputeDefaults(periodEndIso: string) {
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')

  return useQuery<AutoComputeDefaults>({
    queryKey: ['cit-adj-auto-defaults', tenantId, periodEndIso],
    enabled: !!tenantId && !!periodEndIso,
    queryFn: async () => {
      const defaults = emptyDefaults()
      const year = parseInt(periodEndIso.slice(0, 4), 10)
      if (!Number.isFinite(year)) return defaults
      const yearStart = startOfYearIso(year)

      const codes = Array.from(
        new Set(CIT_ADJUSTMENT_CATALOG.flatMap(c => c.sourceAccountCodes)),
      )
      if (codes.length === 0) return defaults

      const { data: accountRows, error: accErr } = await db('accounts')
        .select('id, code')
        .eq('tenant_id', tenantId)
        .in('code', codes)
      if (accErr) throw accErr

      const idToCode = new Map<string, string>()
      for (const a of (accountRows ?? []) as { id: string; code: string }[]) {
        idToCode.set(a.id, a.code)
      }
      if (idToCode.size === 0) return defaults

      const { data: lineRows, error: lineErr } = await db('journal_entry_lines')
        .select(
          'debit_amount, credit_amount, account_id, ' +
            'journal:journal_entries!inner(entry_date, is_posted, is_voided)',
        )
        .eq('tenant_id', tenantId)
        .in('account_id', Array.from(idToCode.keys()))
        .gte('journal.entry_date', yearStart)
        .lte('journal.entry_date', periodEndIso)
        .not('journal.is_posted', 'is', false)
        .not('journal.is_voided', 'is', true)
      if (lineErr) throw lineErr

      const netByCode = new Map<string, number>()
      for (const code of codes) netByCode.set(code, 0)

      for (const l of (lineRows ?? []) as Array<{
        debit_amount: string | number | null
        credit_amount: string | number | null
        account_id: string
      }>) {
        const code = idToCode.get(l.account_id)
        if (!code) continue
        const delta = toNum(l.debit_amount) - toNum(l.credit_amount)
        netByCode.set(code, (netByCode.get(code) ?? 0) + delta)
      }

      for (const entry of CIT_ADJUSTMENT_CATALOG) {
        if (entry.sourceAccountCodes.length === 0) continue
        let sum = 0
        for (const code of entry.sourceAccountCodes) {
          sum += netByCode.get(code) ?? 0
        }
        // Round to 2dp to match the RPC's storage precision.
        defaults[entry.lineType] = Math.round(sum * 100) / 100
      }

      // Capital Allowance — fed by the capital_allowance_entries ledger
      // (IA + AA + investment + balancing allowance − balancing charge).
      const { data: caAmount, error: caErr } = await rpc(
        'compute_capital_allowance_for_period',
        { p_tenant_id: tenantId, p_period_end_date: periodEndIso },
      )
      if (caErr) throw caErr
      defaults['capital_allowance'] = Math.round(toNum(caAmount) * 100) / 100

      return defaults
    },
  })
}

// =============================================================================
// Fetch + roll-forward
// =============================================================================
// Behaviour:
//   * If rows already exist for (tenant, periodEndIso) → return them as-is.
//   * Otherwise compute auto-compute defaults for periodEndIso, look up the
//     MOST RECENT prior period that has rows, and create one draft row per
//     line type:
//       computed_value: from GL (period N)
//       manual_value:   carried over ONLY if the prior period had a manual override
//       label/notes:    carried over from the prior period (so custom labels stick)
//     The roll-forward never carries computed_value — that's recomputed fresh
//     for the new period.

export interface UseCitAdjustmentsResult {
  rows: CitAdjustmentRow[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

export function useCitAdjustments(periodEndIso: string): UseCitAdjustmentsResult {
  const qc = useQueryClient()
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')
  const userId = useAuthStore(s => s.user?.id ?? null)
  const autoQ = useAutoComputeDefaults(periodEndIso)

  const rowsQ = useQuery<CitAdjustmentRow[]>({
    queryKey: ['cit-adjustments', tenantId, periodEndIso],
    enabled: !!tenantId && !!periodEndIso,
    queryFn: async () => {
      const { data, error } = await db('cit_adjustments')
        .select(
          'id, tenant_id, period_end_date, line_type, direction, display_order, ' +
            'label, computed_value, manual_value, effective_value, ' +
            'source_account_codes, notes, accrual_id, created_at, updated_at',
        )
        .eq('tenant_id', tenantId)
        .eq('period_end_date', periodEndIso)
        .order('direction', { ascending: true })
        .order('display_order', { ascending: true })
      if (error) throw error
      return ((data ?? []) as DbAdjustmentRow[]).map(rowFromDb)
    },
  })

  useEffect(() => {
    let cancelled = false
    if (!tenantId || !periodEndIso) return
    if (rowsQ.isLoading || autoQ.isLoading) return
    if (rowsQ.data && rowsQ.data.length > 0) return
    if (!autoQ.data) return

    const defaults = autoQ.data

    void (async () => {
      const { data: priorRows, error: priorErr } = await db('cit_adjustments')
        .select('line_type, manual_value, label, notes, period_end_date')
        .eq('tenant_id', tenantId)
        .lt('period_end_date', periodEndIso)
        .not('accrual_id', 'is', null)
        .order('period_end_date', { ascending: false })
      if (priorErr || cancelled) return

      // Most-recent-period per line_type
      const priorByLine = new Map<
        CitAdjustmentLineType,
        { manualValue: number | null; label: string; notes: string | null }
      >()
      for (const r of (priorRows ?? []) as Array<{
        line_type: CitAdjustmentLineType
        manual_value: string | number | null
        label: string
        notes: string | null
      }>) {
        if (priorByLine.has(r.line_type)) continue
        priorByLine.set(r.line_type, {
          manualValue: r.manual_value === null ? null : toNum(r.manual_value),
          label: r.label,
          notes: r.notes,
        })
      }

      const inserts = CIT_ADJUSTMENT_CATALOG.map(entry => {
        const prior = priorByLine.get(entry.lineType)
        return {
          tenant_id: tenantId,
          period_end_date: periodEndIso,
          line_type: entry.lineType,
          direction: entry.direction,
          display_order: entry.displayOrder,
          label: prior?.label ?? entry.defaultLabel,
          computed_value: defaults[entry.lineType] ?? 0,
          manual_value: prior?.manualValue ?? null,
          source_account_codes:
            entry.sourceAccountCodes.length > 0 ? entry.sourceAccountCodes : null,
          notes: prior?.notes ?? null,
          created_by: userId,
        }
      })

      const { error: insertErr } = await db('cit_adjustments')
        .insert(inserts)
      if (insertErr) {
        // Race: another tab may have inserted concurrently. Just refetch.
        if (!cancelled) {
          qc.invalidateQueries({
            queryKey: ['cit-adjustments', tenantId, periodEndIso],
          })
        }
        return
      }

      if (!cancelled) {
        qc.invalidateQueries({
          queryKey: ['cit-adjustments', tenantId, periodEndIso],
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    qc,
    tenantId,
    userId,
    periodEndIso,
    autoQ.data,
    autoQ.isLoading,
    rowsQ.data,
    rowsQ.isLoading,
  ])

  // Once rows exist, keep computed_value in sync with the latest auto-compute
  // result (drafts only — frozen rows are immutable).
  useEffect(() => {
    if (!tenantId || !periodEndIso) return
    if (rowsQ.isLoading || autoQ.isLoading) return
    if (!rowsQ.data || rowsQ.data.length === 0) return
    if (!autoQ.data) return

    const defaults = autoQ.data
    const stale = rowsQ.data.filter(r => {
      if (r.accrualId) return false
      const expected = defaults[r.lineType] ?? 0
      return Math.abs(r.computedValue - expected) > 0.005
    })
    if (stale.length === 0) return

    let cancelled = false
    void (async () => {
      for (const r of stale) {
        if (cancelled) return
        await db('cit_adjustments')
          .update({ computed_value: defaults[r.lineType] ?? 0 })
          .eq('id', r.id)
      }
      if (!cancelled) {
        qc.invalidateQueries({
          queryKey: ['cit-adjustments', tenantId, periodEndIso],
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [qc, tenantId, periodEndIso, autoQ.data, autoQ.isLoading, rowsQ.data, rowsQ.isLoading])

  return {
    rows: rowsQ.data ?? [],
    isLoading: rowsQ.isLoading || autoQ.isLoading,
    error: (rowsQ.error ?? autoQ.error ?? null) as Error | null,
    refetch: () => {
      qc.invalidateQueries({ queryKey: ['cit-adjustments', tenantId, periodEndIso] })
    },
  }
}

// =============================================================================
// Mutations
// =============================================================================

export interface UpsertCitAdjustmentInput {
  id: string
  manualValue: number | null    // null clears the override → back to computed
}

export function useUpsertCitAdjustment() {
  const qc = useQueryClient()
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')

  return useMutation<CitAdjustmentRow, Error, UpsertCitAdjustmentInput>({
    mutationFn: async ({ id, manualValue }) => {
      const { data, error } = await db('cit_adjustments')
        .update({ manual_value: manualValue })
        .eq('id', id)
        .select(
          'id, tenant_id, period_end_date, line_type, direction, display_order, ' +
            'label, computed_value, manual_value, effective_value, ' +
            'source_account_codes, notes, accrual_id, created_at, updated_at',
        )
        .single()
      if (error) throw error
      return rowFromDb(data as DbAdjustmentRow)
    },
    onSuccess: (row) => {
      qc.invalidateQueries({
        queryKey: ['cit-adjustments', tenantId, row.periodEndDate],
      })
    },
  })
}

export interface DeleteCitAdjustmentInput {
  id: string
  periodEndIso: string
}

export function useDeleteCitAdjustment() {
  const qc = useQueryClient()
  const tenantId = useAuthStore(s => s.tenant?.id ?? '')

  return useMutation<void, Error, DeleteCitAdjustmentInput>({
    mutationFn: async ({ id }) => {
      const { error } = await db('cit_adjustments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: (_, { periodEndIso }) => {
      qc.invalidateQueries({
        queryKey: ['cit-adjustments', tenantId, periodEndIso],
      })
    },
  })
}
